/**
 * use-company-logo — the colour company logo (task #58).
 *
 * GET    /v1/tenants/company-logo (VIEW_RUN_TICKETS) — the stored logo, full size
 * PUT    /v1/tenants/company-logo (MANAGE_TENANT)    — multipart upload / replace
 * DELETE /v1/tenants/company-logo (MANAGE_TENANT)    — remove it
 *
 * The same logo heads the sidebar, so upload and delete invalidate TENANT_BRAND_LOGO_KEY.
 */

import {
  useApiOpts,
  useApiQuery,
  useApiMutation,
  authedFetch,
  TENANT_BRAND_LOGO_KEY,
} from '@lodex-pro/web-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface CompanyLogo {
  /** data:image/png;base64,… */
  readonly dataUrl: string;
  readonly width: number;
  readonly height: number;
  readonly updatedAt: string | null;
}

export interface CompanyLogoResponse {
  readonly logo: CompanyLogo | null;
}

/** Largest file the server takes (2 MB); checked here first so a big file fails fast. */
export const COMPANY_LOGO_MAX_BYTES = 2 * 1024 * 1024;

const COMPANY_LOGO_KEY = ['company-logo'] as const;

/** GET the stored logo. */
export function useCompanyLogo(): ReturnType<typeof useApiQuery<CompanyLogoResponse>> {
  const apiOpts = useApiOpts();
  return useApiQuery<CompanyLogoResponse>(apiOpts, '/v1/tenants/company-logo', {
    queryKey: COMPANY_LOGO_KEY,
    staleTime: 30_000,
  });
}

/**
 * Upload a new logo (multipart). Throws with the server's reason (e.g. "Upload a PNG or JPG
 * image.") so the panel can show it as is.
 */
export function useUploadCompanyLogo(): ReturnType<
  typeof useMutation<CompanyLogoResponse, Error, File>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<CompanyLogoResponse, Error, File>({
    mutationFn: async (file: File): Promise<CompanyLogoResponse> => {
      if (file.size > COMPANY_LOGO_MAX_BYTES) {
        throw new Error('That file is over 2 MB. Use a smaller image.');
      }
      const form = new FormData();
      form.append('file', file);
      const res = await authedFetch(apiOpts, '/v1/tenants/company-logo', {
        method: 'PUT',
        body: form,
      });
      if (!res.ok) {
        let message = 'Logo upload failed. Try again.';
        try {
          const body = (await res.json()) as {
            error?: { message?: string; fields?: ReadonlyArray<{ reason?: string }> };
          };
          const reason = body.error?.fields?.[0]?.reason;
          if (typeof reason === 'string') message = reason;
          else if (typeof body.error?.message === 'string') message = body.error.message;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }
      return (await res.json()) as CompanyLogoResponse;
    },
    onSuccess: (data) => {
      qc.setQueryData(COMPANY_LOGO_KEY, data);
      void qc.invalidateQueries({ queryKey: TENANT_BRAND_LOGO_KEY });
    },
  });
}

/** DELETE the logo; every surface falls back to the company name. */
export function useDeleteCompanyLogo(): ReturnType<
  typeof useApiMutation<void, CompanyLogoResponse>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, CompanyLogoResponse>(apiOpts, '/v1/tenants/company-logo', 'DELETE', {
    onSuccess: () => {
      qc.setQueryData(COMPANY_LOGO_KEY, { logo: null });
      void qc.invalidateQueries({ queryKey: TENANT_BRAND_LOGO_KEY });
    },
  });
}
